// src/components/pages/allocation/partial/PortfolioInput.js

import { useState, useMemo } from "react";
import styles from "./style/PortfolioInput.module.css";
import 'katex/dist/katex.min.css';
import useExtendedDescriptions from "./extendedDescriptions";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";

function PortfolioInput({
  stocks = [],
  optimizeType,
  setOptimizeType,
  userViews,
  setUserViews,
  riskFreeRate,
  setRiskFreeRate,
  dataPeriod,
  setDataPeriod,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  isCustomPeriod,
  setIsCustomPeriod,
  allowShortSelling,
  setAllowShortSelling,
  onCalculate,
}) {
  const [modalContent, setModalContent] = useState("");
  const [showExplanationModal, setShowExplanationModal] = useState(false);
  const [isExpertMode, setIsExpertMode] = useState(false);
  const extendedDescriptions = useExtendedDescriptions();
  const explanationContent = useMemo(() => extendedDescriptions[optimizeType], [optimizeType]);
  const { t } = useTranslation();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // 1. 변수 이름 'tenYearsAgo' -> 'thirtyYearsAgo'로 변경
  const thirtyYearsAgo = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 30);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const handleOptimizeChange = (type, description) => {
    setOptimizeType(type);
    setModalContent(description);
    setShowExplanationModal(false);
  };

  const handleUserViewChange = (stock, value) => {
    setUserViews(prev => ({
      ...prev,
      [stock]: value === "" ? "" : parseFloat(value) || "",
    }));
  };

  const handlePeriodChange = (e) => {
    const value = e.target.value;
    const isCustom = value === "custom"; // 'custom' 선택 여부를 분리
    setIsCustomPeriod(isCustom);

    if (isCustom) {
      // ✅ 'custom' 선택 시: dataPeriod는 null로 설정하고, 시작 날짜/종료 날짜를 30년 전/오늘로 기본값 설정
      setDataPeriod(null); 
      setStartDate(thirtyYearsAgo); 
      setEndDate(today);
    } else {
      // ✅ 1, 3, 20년 등 미리 정의된 기간 선택 시: dataPeriod를 설정하고, 커스텀 날짜 해제
      setDataPeriod(parseInt(value, 10)); 
      setStartDate(null);
      setEndDate(today); // 기존 로직 유지
    }
  };

  const handleCalculateClick = () => {
    if (stocks.length === 0) {
      alert(t("warning1moreTicker"));
      return;
    }
    if (stocks.length > 20) {
      alert(t("warningTooManyTicker"));
      return;
    }
    if (isCustomPeriod && (!startDate || !endDate || startDate > endDate)) {
      alert(t("warning.selectCustomDates") || t("warning.startDateAfterEndDate"));
      return;
    }

    const apiStartDate = isCustomPeriod ? format(startDate, 'yyyy-MM-dd') : '';
    const apiEndDate = isCustomPeriod ? format(endDate, 'yyyy-MM-dd') : '';
    // onCalculate(stocks, optimizeType, riskFreeRate, userViews, allowShortSelling, dataPeriod, apiStartDate, apiEndDate);
    onCalculate(stocks, optimizeType, riskFreeRate, userViews, allowShortSelling, dataPeriod, startDate, endDate);
  };

  const getButtonClass = (type) => `${styles.button} ${optimizeType === type ? styles.active : ''}`;

  return (
    <div className={styles['portfolio-input']}>
      <h2>{t("selectAllocMethod")}</h2>
      <br />
      <div className={styles['button-group']}>
        <button
          className={getButtonClass("minVar")}
          onClick={() => handleOptimizeChange("minVar", t("minVarExplanation"))}
        >
          {t("minVarButtonDesc")} <br />
          <small>(Minimum Variance Portfolio)</small>
        </button>
        <button
          className={getButtonClass("riskParity")}
          onClick={() => handleOptimizeChange("riskParity", t("riskPartiyExplanation"))}
        >
          {t("riskPartiyButtonDesc")} <br />
          <small>(Risk Parity)</small>
        </button>
        <button
          className={getButtonClass("blackLiterman")}
          onClick={() => handleOptimizeChange("blackLiterman", t("blackLittermanExplanation"))}
        >
          {t("blackLittermanButtonDesc")} <br />
          <small>(Black-Litterman Model)</small>
        </button>
        <button
          className={getButtonClass("maxSharpe")}
          onClick={() => handleOptimizeChange("maxSharpe", t("maxSharpeExplanation"))}
        >
          {t("maxSharpeButtonDesc")} <br />
          <small>(Max Sharpe Ratio)</small>
        </button>
      </div>

      <div className={styles['modal-container']}>
        {modalContent && <div className={styles.modal}>{modalContent}</div>}
      </div>

      <div className={styles['explanation-container']}>
        <button
          onClick={() => setShowExplanationModal(prev => !prev)}
          className={styles['explanation-btn']}
        >
          {showExplanationModal ? t("closeExplanation") : t("openExplanation")}
        </button>
      </div>

      {showExplanationModal && (
        <div className={styles['explanation-modal-wrapper']}>
          <div className={styles['explanation-modal']}>
            <div className={styles['explanation-content']}>{explanationContent}</div>
          </div>
        </div>
      )}

      

      <div className={styles['settings-container']}>
        <div className={styles['data-period']}>
          <span>{t("dataperiod")}</span>
          <select
            value={isCustomPeriod ? "custom" : dataPeriod}
            onChange={handlePeriodChange}
          >
            {Array.from({ length: 10 }, (_, i) => i + 1).map(year => (
              <option key={year} value={year}>{year} {t("year")}</option>
            ))}
            {/* 20년, 30년 옵션 추가*/}
            {[20, 30].map(year => (
                <option key={year} value={year}>{year} {t("year")}</option>
            ))}
            <option value="custom">{t("backtest.custom")}</option>
          </select>
        </div>

        <div className={styles['short-selling-toggle']}>
          <span>{t("allowshort")}</span>
          <label className={styles.switch}>
            <input
              type="checkbox"
              checked={allowShortSelling}
              onChange={() => setAllowShortSelling(prev => !prev)}
            />
            <span className={`${styles.slider} ${styles.round}`}></span>
          </label>
        </div>

        {(optimizeType === "maxSharpe") && (
          <div className={styles['expert-mode-toggle']}>
            <span>{t("AdvancedSetting")}</span>
            <label className={styles.switch}>
              <input
                type="checkbox"
                checked={isExpertMode}
                onChange={() => setIsExpertMode(prev => !prev)}
              />
              <span className={`${styles.slider} ${styles.round}`}></span>
            </label>
          </div>
        )}
      </div>

        {isCustomPeriod && (
        <div className={styles['custom-date-pickers']}>
          <DatePicker
            selected={startDate}
            onChange={setStartDate}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            dateFormat="yyyy-MM-dd"
            // 2. minDate 속성 수정 (tenYearsAgo -> thirtyYearsAgo)
            minDate={thirtyYearsAgo}
            maxDate={endDate || today}
            placeholderText="YYYY-MM-DD"
            className={styles['date-input']}
            showMonthDropdown
            showYearDropdown
            scrollableYearDropdown
          />
          <span> ~ </span>
          <DatePicker
            selected={endDate}
            onChange={setEndDate}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            // 3. minDate 속성 수정 (tenYearsAgo -> thirtyYearsAgo)
            minDate={startDate || thirtyYearsAgo}
            maxDate={today}
            dateFormat="yyyy-MM-dd"
            placeholderText="YYYY-MM-DD"
            className={styles['date-input']}
            showMonthDropdown
            showYearDropdown
            scrollableYearDropdown
          />
        </div>
      )}

      {(optimizeType === "blackLiterman" || optimizeType === "maxSharpe") && (
        <div className={styles['riskfree-blackliterman']}>
          <div className={styles['blackliterman-input']}>
            <h3>{t("enterExpectedReturn")}</h3>
            {optimizeType === "blackLiterman" && (
              <p className={styles['view-info']}>{t("enterExpectedReturnExplanationBL")}</p>
            )}
            {optimizeType === "maxSharpe" && (
              <p className={styles['view-info']}>{t("enterExpectedReturnExplanationMaxSharpe")}</p>
            )}
            {stocks.map(stock => (
              <div key={stock} className={styles['stock-view']}>
                <label>{stock}</label>
                <input
                  type="number"
                  placeholder={t("enterExpectedReturnExample")}
                  value={userViews[stock] || ""}
                  onChange={e => handleUserViewChange(stock, e.target.value)}
                  step="0.1"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {optimizeType === "maxSharpe" && isExpertMode && (
        <div className={styles['riskfree-blackliterman']}>
          <div className={styles['risk-free-rate']}>
            <label>{t("riskfreerate")}</label>
            <p>{t("riskfreerateExplanation")}</p>
            <input
              type="number"
              value={riskFreeRate}
              onChange={e => setRiskFreeRate(parseFloat(e.target.value) || 0)}
              step="0.01"
            />
          </div>
        </div>
      )}
      <br />
      <div className={styles['button-container']}>
        <button className={styles['calculate-button']} onClick={handleCalculateClick}>
          {t("DoassetAllocation")}
        </button>
      </div>
    </div>
  );
}

export default PortfolioInput;